import {
  CircleAlert,
  Mail,
  MailOpen,
  Paperclip,
  Send,
  type LucideIcon,
} from "lucide-react";
import { useGetList, useLocaleState, useTranslate } from "ra-core";
import { useState } from "react";
import { cn } from "@/lib/utils";

import type { MailMessage } from "../types";
import { MailBody } from "./MailBody";

export type MailFilter =
  | { deal_id: MailMessage["deal_id"] }
  | { lead_id: MailMessage["lead_id"] }
  | { contact_id: MailMessage["contact_id"] };

const PER_PAGE = 50;

const sizeLabel = (n: number) =>
  n >= 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} МБ`
    : `${Math.max(1, Math.round(n / 1024))} КБ`;

/**
 * The letters of a card, newest first (who sees them: crm/db/17_mail.sql).
 * A row: the direction, the subject, from or to, the date and the status;
 * a click opens the letter.
 */
export const MailTimeline = ({ filter }: { filter: MailFilter }) => {
  const translate = useTranslate();
  const { data, total, isPending, error } = useGetList<MailMessage>(
    "mail_messages",
    {
      filter,
      sort: { field: "date", order: "DESC" },
      pagination: { page: 1, perPage: PER_PAGE },
    },
  );
  if (isPending) return null;
  if (error || !data?.length) {
    return (
      <p className="text-sm text-muted-foreground py-1">
        {translate("crm.mail.empty")}
      </p>
    );
  }
  return (
    <>
      <ul
        className="flex flex-col divide-y"
        aria-label={translate("crm.mail.title")}
      >
        {data.map((mail) => (
          <MailRow key={mail.id} mail={mail} />
        ))}
      </ul>
      {total != null && total > data.length ? (
        <p className="text-xs text-muted-foreground pt-2">
          {translate("crm.mail.only_latest", { smart_count: data.length })}
        </p>
      ) : null}
    </>
  );
};

const MailRow = ({ mail }: { mail: MailMessage }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const [open, setOpen] = useState(false);
  const failed = mail.status === "failed";
  const Icon: LucideIcon = failed
    ? CircleAlert
    : mail.direction === "in"
      ? open
        ? MailOpen
        : Mail
      : Send;
  const when = new Date(mail.date).toLocaleString(locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  const who =
    mail.direction === "in"
      ? `${translate("crm.mail.from")}: ${mail.from_name || mail.from_addr}`
      : `${translate("crm.mail.to")}: ${[...mail.to_addrs, ...mail.cc].join(", ")}`;
  // a sent letter with error: the addresses the server refused
  const status =
    mail.status === "sent" && mail.error
      ? translate("crm.mail.status_sent_except", { list: mail.error })
      : translate(`crm.mail.status.${mail.status}`);

  return (
    <li className="py-2 min-w-0" data-mail={mail.id}>
      <button
        type="button"
        className="flex w-full items-start gap-3 text-left min-w-0"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Icon
          className={cn(
            "size-4 shrink-0 mt-0.5",
            failed ? "text-destructive" : "text-muted-foreground",
          )}
          aria-label={translate(`crm.mail.direction.${mail.direction}`)}
          role="img"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">
            {mail.subject || translate("crm.mail.no_subject")}
          </div>
          <div className="text-xs text-muted-foreground truncate">{who}</div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-xs text-muted-foreground tabular-nums">
            {when}
          </div>
          <div
            className={cn(
              "text-xs",
              failed ? "text-destructive" : "text-muted-foreground",
            )}
            data-mail-status={mail.status}
          >
            {mail.attachments?.length ? (
              <Paperclip className="inline size-3 mr-1" aria-hidden />
            ) : null}
            {status}
          </div>
        </div>
      </button>
      {open ? (
        <div className="mt-2 flex flex-col gap-2 min-w-0 sm:pl-7">
          {failed && mail.error ? (
            <p className="text-xs text-destructive">{mail.error}</p>
          ) : null}
          {mail.direction === "out" ? (
            <p className="text-xs text-muted-foreground break-all">
              {translate("crm.mail.from")}: {mail.from_name ?? ""}{" "}
              {`<${mail.from_addr}>`}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground break-all">
              {translate("crm.mail.from")}: {mail.from_addr}
            </p>
          )}
          <MailBody mail={mail} />
          {mail.attachments?.length ? (
            <div className="text-xs text-muted-foreground flex flex-col gap-1">
              <span>{translate("crm.mail.attachments")}:</span>
              <ul className="flex flex-col gap-0.5">
                {mail.attachments.map((a, i) => (
                  <li key={i} className="break-all">
                    {a.name} · {sizeLabel(a.size)}
                  </li>
                ))}
              </ul>
              {mail.direction === "in" ? (
                <span>{translate("crm.mail.files_not_kept")}</span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
};
